import styled from 'styled-components';

import { fieldContainer, fieldLabel, fieldMessage } from '../base';

/**
 * The tree select — the parent-category picker, and its only call site.
 *
 * antd's `TreeSelect` draws its own control, so this is the one form component
 * whose geometry has to be written against antd's classes rather than
 * composed from `ctrlBase`. The values are the same ones `ctrlBase` sets: the
 * shared control height, radius, hairline and focus ring, instead of a 50px
 * box on a 10px radius with 20px of side padding, 14px text and a
 * `--primary-green` border on hover.
 *
 * Everything else here — `Input`, `icon`, `iconSelect`, `dropDownItems` — was
 * scaffolding from a hand-rolled version this component no longer renders.
 */

export const inputContainer = styled.div`
  ${fieldContainer}
`;

export const Label = styled('label')<{ labelColor?: string | undefined }>`
  ${fieldLabel}
  cursor: pointer;
`;

export const message = styled.span`
  ${fieldMessage}
`;

export const InputBox = styled.div`
  position: relative;

  && .ant-select {
    width: 100%;
    height: var(--h-ctrl);
    font-family: var(--font-ui);
    font-size: var(--fs-body);
  }

  && .ant-select .ant-select-selector {
    height: var(--h-ctrl);
    padding: 0 var(--s3);
    background: var(--surface);
    border: 1px solid var(--line-strong);
    border-radius: var(--r-sm);
    box-shadow: none;
    color: var(--text);
    cursor: pointer;
    transition:
      border-color 0.12s,
      box-shadow 0.12s;
  }

  && .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: var(--text-faint);
  }

  && .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-soft);
  }

  && .ant-select-selection-placeholder,
  && .ant-select-selection-item {
    display: flex;
    align-items: center;
    padding: 0;
    font-size: var(--fs-body);
    line-height: var(--lh-body);
  }

  && .ant-select-selection-placeholder {
    color: var(--text-faint);
  }

  && .ant-select-selection-search-input {
    height: 100% !important;
    padding: 0 !important;
  }

  && .ant-select-arrow,
  && .ant-select-clear {
    color: var(--text-muted);
    inset-inline-end: var(--s3);
  }

  && .ant-select-disabled .ant-select-selector {
    background: var(--surface-alt);
    color: var(--text-faint);
    cursor: not-allowed;
  }

  /* The error state comes from the container, as it does for every control. */
  .error && .ant-select .ant-select-selector,
  &&.error .ant-select .ant-select-selector {
    border-color: var(--danger);
    background: var(--danger-tint);
  }
`;
